import { PatientVo, QuestionnaireVo } from '@app/core/services';
import { Observable, of } from 'rxjs';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import * as moment from 'moment';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export function generateQuestionnairePdf$(
  questionnaire: QuestionnaireVo,
  patient: PatientVo,
  includeWatermark = false,
  password?: string
): Observable<pdfMake.TCreatedPdf> {
  return of(questionnaire).pipe(
    take(1),
    map(q => {
      const tableBody = [
        [
          { text: 'No.', style: 'tableHeader' },
          { text: 'Question', style: 'tableHeader' },
          { text: 'Answer', style: 'tableHeader', alignment: 'center' },
        ],
      ];

      let rowIndex = 1;

      q.Questions.forEach((question, index) => {
        if (question.Type === 'multiple_checkbox' && Array.isArray(question.SubQuestions)) {
          // Add group header row
          tableBody.push([
            { text: `${rowIndex++}`, style: 'tableCell', alignment: 'center' },
            { text: question.Question, style: 'tableCellBold' },
            { text: '', style: 'tableCell' },
          ]);

          // Add each selected sub-question
          question.SubQuestions.forEach(sub => {
            tableBody.push([
              { text: '', style: 'tableCell' },
              { text: sub.Question, style: 'tableCell' },
              { text: sub.Answer ? 'Yes' : 'No', style: 'tableCell', alignment: 'center' },
            ]);
          });
        } else {
          const answerText = question.Answer?.AnswerText ?? '—';
          tableBody.push([
            { text: `${rowIndex++}`, style: 'tableCell', alignment: 'center' },
            { text: question.Question, style: 'tableCell' },
            { text: answerText, style: 'tableCell', alignment: 'center' },
          ]);
        }
      });

      const answeredQuestions = q.Questions.filter(
        q => q.Answer?.Value != null || q.Answer?.Value != undefined || q.Type === 'multiple_checkbox'
      ).length;

      const docDefinition: pdfMake.TDocumentDefinitions = {
        content: [
          { text: q.Title ?? 'Questionnaire', style: 'formTitle' },
          { text: `Patient: ${patient.PatientDetails.FirstName} ${patient.PatientDetails.Surname}`, style: 'patientDetails' },
          { text: `Sex: ${patient.PatientDetails.Gender}`, style: 'patientDetails' },
          { text: `DOB: ${moment(patient.PatientDetails.DateOfBirth).format('DD/MM/YYYY')}`, style: 'patientDetails' },
          { text: `Questions completed: ${answeredQuestions}/${q.Questions.length}`, style: 'formMeta' },

          ...(q.Score != null
            ? [
                {
                  columns: [
                    { text: `Score: ${q.Score} / ${q.MaxScore ?? ''}`, style: 'formMeta' },
                    { text: `Severity: ${q.Severity ?? ''}`, style: 'formMeta', alignment: 'right' },
                  ],
                  margin: [0, 0, 0, 10],
                },
              ]
            : []),

          {
            table: {
              widths: [20, '*', 150],
              body: tableBody,
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#ccc',
              vLineColor: () => '#ccc',
              paddingTop: () => 6,
              paddingBottom: () => 6,
              paddingLeft: () => 4,
              paddingRight: () => 4,
            },
          },
        ],
        styles: {
          formTitle: {
            fontSize: 14,
            bold: false,
            margin: [0, 0, 0, 4],
          },
          patientDetails: {
            fontSize: 10,
          },
          formMeta: {
            fontSize: 10,
            italics: true,
            color: '#444',
          },
          tableHeader: {
            fontSize: 10,
            bold: true,
            fillColor: '#f0f0f0',
            margin: [0, 2, 0, 2],
          },
          tableCell: {
            fontSize: 10,
          },
          tableCellBold: {
            fontSize: 10,
            bold: true,
          },
        },

        ...(includeWatermark && {
          watermark: {
            text: `${patient.PatientDetails.FirstName} ${patient.PatientDetails.Surname} ${moment().format('DD-MM-YYYY')}`,
            color: '#aaaaaa',
            opacity: 0.2,
            italics: true,
            fontSize: 48,
            bold: false,
            angle: 45,
          } as any,
        }),

        ...(!!password
          ? {
              userPassword: password,
              permissions: {
                printing: 'highResolution',
                modifying: false,
                copying: true,
              },
            }
          : {}),
      };

      return docDefinition;
    }),
    switchMap(
      docDefinition =>
        new Observable<pdfMake.TCreatedPdf>(observer => {
          const pdf = pdfMake.createPdf(docDefinition);
          observer.next(pdf);
          observer.complete();
        })
    ),
    shareReplay()
  );
}
